import {
  CANONICAL_WHATSAPP_TEMPLATE,
  CANONICAL_WHATSAPP_TEMPLATE_EXAMPLE,
} from '@borradh-workspace/api-client';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { PageShell } from '@/components/app/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  type WhatsappTemplate,
  useRefreshWhatsappTemplates,
  useWhatsappTemplates,
  whatsappTemplateStatusLabels,
} from '@/features/campaigns';
import {
  useCreateWhatsAppTemplate,
  useDeleteWhatsAppTemplate,
  useListWhatsAppAccounts,
} from '@/features/integrations/api';

/**
 * `/dashboard/settings/message-templates` — manage the org's WhatsApp message
 * templates (list / create / delete / re-sync from Meta). Templates are
 * required to start WhatsApp conversations outside the 24-hour customer
 * service window (e.g. campaign sends) and go through Meta review.
 */
export const Route = createFileRoute(
  '/_authed/dashboard/settings/message-templates'
)({
  component: MessageTemplatesSettingsPage,
});

type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

const STATUS_BADGE_VARIANT: Record<
  WhatsappTemplate['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  approved: 'default',
  pending: 'secondary',
  rejected: 'destructive',
  paused: 'outline',
  disabled: 'outline',
};

function MessageTemplatesSettingsPage() {
  const { accounts, isLoading: isLoadingAccounts } = useListWhatsAppAccounts();
  const activeAccount = accounts.find((account) => account.isActive);

  return (
    <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
      <title>Message templates | Borrah</title>

      <header className="space-y-1">
        <h2 className="font-semibold text-xl tracking-tight">
          Message templates
        </h2>
        <p className="text-muted-foreground text-sm">
          WhatsApp requires a pre-approved template to message clients outside
          the 24-hour reply window (campaign sends, reminders). Templates are
          reviewed by Meta before they can be used.
        </p>
      </header>

      {isLoadingAccounts ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : activeAccount ? (
        <TemplatesCard accountId={activeAccount.id} />
      ) : (
        <ConnectWhatsappEmptyState />
      )}
    </PageShell>
  );
}

// --- Not connected: point people at the integrations page ---

function ConnectWhatsappEmptyState() {
  return (
    <Card>
      <CardContent>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon" className="bg-transparent">
              <img src="/wa-icon.svg" alt="WhatsApp" className="size-10" />
            </EmptyMedia>
            <EmptyTitle>No WhatsApp account connected</EmptyTitle>
            <EmptyDescription>
              Connect a WhatsApp Business account to create and manage message
              templates for campaign sends.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              asChild
              size="lg"
              className="w-full max-w-xs bg-red-600 text-white hover:bg-red-700"
            >
              <Link to="/dashboard/settings/integrations">
                <img src="/wa-icon.svg" alt="" className="size-5" />
                <Pencil className="size-4" />
                Connect WhatsApp
              </Link>
            </Button>
          </EmptyContent>
        </Empty>
      </CardContent>
    </Card>
  );
}

// --- Connected: template list + create / delete / refresh ---

function TemplatesCard({ accountId }: { accountId: string }) {
  const { templates, isLoading } = useWhatsappTemplates();
  const { refreshTemplates, isRefreshing } = useRefreshWhatsappTemplates();

  const [createOpen, setCreateOpen] = useState(false);
  // Set when "Re-apply" is clicked on a rejected template — prefills the same
  // create dialog so the org can edit the content before resubmitting rather
  // than starting from a blank form.
  const [reapplyTemplate, setReapplyTemplate] =
    useState<WhatsappTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] =
    useState<WhatsappTemplate | null>(null);

  // Create/delete happen against Meta; the campaigns-side template cache is a
  // DB sync, so re-pull from Meta afterwards to reflect the change.
  const { deleteTemplate, isDeleting } = useDeleteWhatsAppTemplate(accountId, {
    onSuccess: () => {
      setTemplateToDelete(null);
      refreshTemplates();
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Templates</CardTitle>
            <CardDescription>
              Synced from your WhatsApp Business account.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refreshTemplates()}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={isRefreshing ? 'size-4 animate-spin' : 'size-4'}
              />
              Refresh from Meta
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              New template
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : templates.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground text-sm">
            No templates yet. Create one to send WhatsApp campaigns, or refresh
            if you already have templates in Meta Business Manager.
          </p>
        ) : (
          <ul className="space-y-2">
            {templates.map((template) => (
              <li
                key={template.id}
                className="flex items-start justify-between gap-3 rounded-lg border p-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{template.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {template.languageCode}
                    </span>
                    {template.category && (
                      <Badge variant="outline">
                        {template.category.toLowerCase()}
                      </Badge>
                    )}
                    <Badge variant={STATUS_BADGE_VARIANT[template.status]}>
                      {whatsappTemplateStatusLabels[template.status]}
                    </Badge>
                  </div>
                  <p className="whitespace-pre-wrap text-muted-foreground text-xs">
                    {template.body}
                  </p>
                  {template.status === 'rejected' && (
                    <div className="flex items-start gap-1.5 rounded-md bg-destructive/10 p-2 text-destructive text-xs">
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        {template.rejectionReason
                          ? `Meta's reason: ${template.rejectionReason}`
                          : "Meta didn't include a specific reason with this rejection."}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {template.status === 'rejected' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReapplyTemplate(template)}
                    >
                      <RotateCcw className="size-4" />
                      Re-apply
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete template ${template.name}`}
                    onClick={() => setTemplateToDelete(template)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <CreateTemplateDialog
        accountId={accountId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => refreshTemplates()}
      />

      {/* Same dialog, prefilled from the rejected template — edit and resubmit
          under the same name. `createWhatsappTemplate` registers on Meta; the
          next refresh (or the status webhook) reconciles the cache row via its
          existing (org, name, language) upsert, so this never duplicates it. */}
      <CreateTemplateDialog
        key={reapplyTemplate?.id ?? 'reapply'}
        accountId={accountId}
        open={reapplyTemplate !== null}
        onOpenChange={(open) => {
          if (!open) setReapplyTemplate(null);
        }}
        onCreated={() => {
          setReapplyTemplate(null);
          refreshTemplates();
        }}
        initial={
          reapplyTemplate
            ? reapplyTemplate.name === CANONICAL_WHATSAPP_TEMPLATE.name
              ? // The canonical template's shape can move (e.g. to add static
                // business context or fix a rejection cause) independently of
                // any one org's stale, already-rejected DB row — re-applying it
                // should resubmit the CURRENT canonical content, not repeat the
                // exact content Meta just rejected.
                {
                  name: CANONICAL_WHATSAPP_TEMPLATE.name,
                  category: CANONICAL_WHATSAPP_TEMPLATE.category,
                  language: CANONICAL_WHATSAPP_TEMPLATE.languageCode,
                  body: CANONICAL_WHATSAPP_TEMPLATE.body,
                  bodyExample: [...CANONICAL_WHATSAPP_TEMPLATE_EXAMPLE],
                }
              : {
                  name: reapplyTemplate.name,
                  category:
                    (reapplyTemplate.category as TemplateCategory | null) ??
                    'MARKETING',
                  language: reapplyTemplate.languageCode,
                  body: reapplyTemplate.body,
                }
            : undefined
        }
        title="Re-apply for a template"
        description="Edit the content Meta rejected, then resubmit for review."
        submitLabel="Resubmit template"
      />

      <ConfirmDeleteDialog
        description="This removes the template from your WhatsApp Business account. Campaigns using it will no longer be able to send."
        isPending={isDeleting}
        onConfirm={() => {
          if (templateToDelete) deleteTemplate(templateToDelete.name);
        }}
        onOpenChange={(open) => {
          if (!open) setTemplateToDelete(null);
        }}
        open={templateToDelete !== null}
        title={<>Delete &ldquo;{templateToDelete?.name}&rdquo;?</>}
      />
    </Card>
  );
}

// --- Create dialog ---

interface CreateTemplateDialogInitial {
  name: string;
  category: TemplateCategory;
  language: string;
  body: string;
  bodyExample?: string[];
}

/** Count the unique `{{n}}` positional placeholders in a template body. */
function countPlaceholders(body: string): number {
  const indices = new Set<number>();
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let match: RegExpExecArray | null = re.exec(body);
  while (match !== null) {
    indices.add(Number(match[1]));
    match = re.exec(body);
  }
  return indices.size;
}

interface CreateTemplateDialogProps {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** Prefills the form — used by "Re-apply" on a rejected template. */
  initial?: CreateTemplateDialogInitial;
  title?: string;
  description?: string;
  submitLabel?: string;
}

function CreateTemplateDialog({
  accountId,
  open,
  onOpenChange,
  onCreated,
  initial,
  title = 'New message template',
  description = 'Templates are submitted to Meta for review; approval usually takes a few minutes to a few hours.',
  submitLabel = 'Create template',
}: CreateTemplateDialogProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState<TemplateCategory>(
    initial?.category ?? 'MARKETING'
  );
  const [language, setLanguage] = useState(initial?.language ?? 'en_US');
  const [body, setBody] = useState(initial?.body ?? '');
  // Meta requires one example value per `{{n}}` placeholder at submission —
  // a template with placeholders and no examples is a common hard-reject.
  const [exampleValues, setExampleValues] = useState<string[]>(
    initial?.bodyExample ?? []
  );

  const { createTemplate, isCreating } = useCreateWhatsAppTemplate(accountId, {
    onSuccess: () => {
      setName('');
      setBody('');
      setExampleValues([]);
      onOpenChange(false);
      onCreated();
    },
  });

  const placeholderCount = countPlaceholders(body);
  const examplesComplete =
    placeholderCount === 0 ||
    (exampleValues.length === placeholderCount &&
      exampleValues.every((v) => v.trim().length > 0));

  function handleBodyChange(next: string) {
    setBody(next);
    const count = countPlaceholders(next);
    setExampleValues((prev) =>
      Array.from({ length: count }, (_, i) => prev[i] ?? '')
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Template name</FieldLabel>
            <Input
              placeholder="e.g. appointment_reminder"
              value={name}
              onChange={(e) =>
                setName(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                )
              }
            />
            <p className="text-muted-foreground text-xs">
              Lowercase letters, numbers, and underscores only.
            </p>
          </Field>

          <Field>
            <FieldLabel>Category</FieldLabel>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as TemplateCategory)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MARKETING">Marketing</SelectItem>
                <SelectItem value="UTILITY">Utility</SelectItem>
                <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Language</FieldLabel>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en_US">English (US)</SelectItem>
                <SelectItem value="en_GB">English (UK)</SelectItem>
                <SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
                <SelectItem value="pt_BR">Portuguese (BR)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Body</FieldLabel>
            <Textarea
              placeholder="e.g. Hi {{1}}, your appointment is confirmed for {{2}}."
              value={body}
              onChange={(e) => handleBodyChange(e.target.value)}
              rows={4}
            />
            <p className="text-muted-foreground text-xs">
              Use {'{{1}}'}, {'{{2}}'}, etc. for variables.
            </p>
          </Field>

          {placeholderCount > 0 && (
            <Field>
              <FieldLabel>Example values</FieldLabel>
              <p className="text-muted-foreground text-xs">
                Meta requires a realistic example for every variable before it
                will review the template.
              </p>
              <div className="space-y-2">
                {Array.from({ length: placeholderCount }, (_, i) => (
                  <Input
                    key={i}
                    placeholder={`Example for {{${i + 1}}}`}
                    value={exampleValues[i] ?? ''}
                    onChange={(e) =>
                      setExampleValues((prev) => {
                        const next = [...prev];
                        next[i] = e.target.value;
                        return next;
                      })
                    }
                  />
                ))}
              </div>
            </Field>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              createTemplate({
                name,
                category,
                language,
                body,
                bodyExample: placeholderCount > 0 ? exampleValues : undefined,
              })
            }
            disabled={
              isCreating || !name.trim() || !body.trim() || !examplesComplete
            }
          >
            {isCreating && <Loader2 className="size-4 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
